/**
 * SupabaseWebhooksRepository
 * Responsabilidade: Acesso a dados de webhooks no Supabase
 * Princípios: Single Responsibility, Dependency Inversion
 */

import { supabase } from '../../lib/supabase';
import { Webhook } from '../../types';

export class SupabaseWebhooksRepository {
    async findAll(organizationId: string): Promise<Webhook[]> {
        const { data, error } = await supabase
            .from('webhooks')
            .select('*')
            .eq('organization_id', organizationId)
            .order('created_at', { ascending: false });

        if (error) {
            console.error('[WebhooksRepository] Error fetching webhooks:', error);
            throw new Error(`Erro ao buscar webhooks: ${error.message}`);
        }

        return (data || []).map(this.mapToEntity);
    }

    async findById(id: string, organizationId: string): Promise<Webhook | null> {
        const { data, error } = await supabase
            .from('webhooks')
            .select('*')
            .eq('id', id)
            .eq('organization_id', organizationId)
            .single();

        if (error) {
            if (error.code === 'PGRST116') return null;
            console.error('[WebhooksRepository] Error fetching webhook:', error);
            throw new Error(`Erro ao buscar webhook: ${error.message}`);
        }

        return this.mapToEntity(data);
    }

    async create(webhook: Omit<Webhook, 'id' | 'createdAt'>, organizationId: string): Promise<Webhook> {
        const { data, error } = await supabase
            .from('webhooks')
            .insert([{
                organization_id: organizationId,
                name: webhook.name,
                url: webhook.url,
                events: webhook.events || [],
                active: webhook.isActive !== undefined ? webhook.isActive : true
            }])
            .select()
            .single();

        if (error) {
            console.error('[WebhooksRepository] Error creating webhook:', error);
            throw new Error(`Erro ao criar webhook: ${error.message}`);
        }

        return this.mapToEntity(data);
    }

    async update(id: string, webhook: Partial<Webhook>, organizationId: string): Promise<Webhook> {
        const updateData: any = {};
        if (webhook.name !== undefined) updateData.name = webhook.name;
        if (webhook.url !== undefined) updateData.url = webhook.url;
        if (webhook.events !== undefined) updateData.events = webhook.events;
        if (webhook.isActive !== undefined) updateData.active = webhook.isActive;

        const { data, error } = await supabase
            .from('webhooks')
            .update(updateData)
            .eq('id', id)
            .eq('organization_id', organizationId)
            .select()
            .single();

        if (error) {
            console.error('[WebhooksRepository] Error updating webhook:', error);
            throw new Error(`Erro ao atualizar webhook: ${error.message}`);
        }

        return this.mapToEntity(data);
    }

    async delete(id: string, organizationId: string): Promise<void> {
        const { error } = await supabase
            .from('webhooks')
            .delete()
            .eq('id', id)
            .eq('organization_id', organizationId);

        if (error) {
            console.error('[WebhooksRepository] Error deleting webhook:', error);
            throw new Error(`Erro ao deletar webhook: ${error.message}`);
        }
    }

    private mapToEntity(data: any): Webhook {
        return {
            id: data.id,
            name: data.name,
            url: data.url,
            events: data.events || [],
            isActive: data.active,
            createdAt: data.created_at
        };
    }
}

export const webhooksRepository = new SupabaseWebhooksRepository();
